import { useCallback } from 'react';

import { useTranslation } from '@/hooks/useTranslation';

import { BrowseActiveFilter, ItemsListMeta } from '../../types';
import { FoundItemsLabel } from './FoundItemsLabel';
import { useSearchKeyword } from './useSearchKeyword';

const ERROR_CODE_SEARCH_QUERY_INVALID = 301;

/** Angular `search-filter.tpl.html` uses `validation-warning` for code 301; OpenSearch internals are an error. */
export type SearchValidationSeverity = 'warning' | 'error';

const VALIDATION_MESSAGE_CLASS: Record<SearchValidationSeverity, string> = {
    warning: 'validation validation-warning tw-bg-warning-lighter tw-text-warning-dark',
    error: 'validation validation-error tw-bg-error-lighter tw-text-error-dark',
};

export interface SearchFilterBarProps {
    keyword?: string;
    onKeywordChange: (keyword: string) => void;
    activeFilters?: Record<string, BrowseActiveFilter>;
    onRemoveFilter?: (filter: BrowseActiveFilter) => void;
    itemsMeta?: ItemsListMeta | null;
    countingItems?: boolean;
    onRefreshCount?: () => void;
    errorCode?: number;
    errorMessage?: string;
    /** `warning` = invalid query (Angular default). `error` = internal search failure. */
    errorSeverity?: SearchValidationSeverity;
}

/**
 * Bare browse search bar: keyword input, active-filter tags (stub for
 * LAX-10134), and the "N items found" label from `search-filter.tpl.html`.
 */
export function SearchFilterBar({
    keyword = '',
    onKeywordChange,
    activeFilters = {},
    onRemoveFilter,
    itemsMeta,
    countingItems = false,
    onRefreshCount,
    errorCode,
    errorMessage,
    errorSeverity = 'warning',
}: SearchFilterBarProps) {
    const { t } = useTranslation();

    const handleDebouncedKeywordChange = useCallback(
        (value: string) => {
            if (value !== keyword) {
                onKeywordChange(value);
            }
        },
        [keyword, onKeywordChange],
    );

    const { inputValue, setInputValue } = useSearchKeyword({
        keyword,
        onDebouncedKeywordChange: handleDebouncedKeywordChange,
    });

    const visibleFilters = Object.values(activeFilters).filter((filter) => filter.name);

    return (
        <div className="tw-mb-2">
            <div className="tw-border tw-border-primary tw-rounded tw-bg-fill-primary tw-min-h-9 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-px-2 tw-py-1">
                {visibleFilters.map((filter) => (
                    <span
                        key={filter.type}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-bg-secondary-lighter tw-px-2 tw-py-0.5 tw-text-t-sm"
                    >
                        <span className="search-filter-tag">{filter.name}</span>
                        {onRemoveFilter && (
                            <button
                                type="button"
                                className="ui-select-match-close close-tag tw-leading-none"
                                aria-label={t('MONITOR.CLEAR_FILTERS')}
                                onClick={() => onRemoveFilter(filter)}
                            >
                                &times;
                            </button>
                        )}
                    </span>
                ))}
                <input
                    id="item-search-input"
                    type="text"
                    className="ui-select-search input-xs tw-flex-1 tw-min-w-[8rem] tw-border-0 tw-bg-transparent tw-outline-none tw-text-t-sm"
                    placeholder={t('SEARCH_PLACEHOLDER')}
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    aria-label={t('SEARCH_PLACEHOLDER')}
                />
            </div>

            {errorCode === ERROR_CODE_SEARCH_QUERY_INVALID && errorMessage && (
                <div className="tw-mt-1">
                    <span
                        id="search-validation-message"
                        className={VALIDATION_MESSAGE_CLASS[errorSeverity]}
                    >
                        {errorMessage}
                    </span>
                </div>
            )}

            {itemsMeta && itemsMeta.visibleRowCount > 0 && (
                <div className="tw-mt-1">
                    <FoundItemsLabel
                        visibleRowCount={itemsMeta.visibleRowCount}
                        numberFound={itemsMeta.numberFound}
                        maxNumberFound={itemsMeta.maxNumberFound}
                        countingItems={countingItems}
                        hasCursor={itemsMeta.hasCursor}
                        onRefreshCount={onRefreshCount}
                    />
                </div>
            )}
        </div>
    );
}
